import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Upload,
  Download,
  Trash2,
  FileSpreadsheet,
  Database,
  AlertTriangle,
  RefreshCcw,
  Check,
  Loader2,
  Calendar,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DATA_TYPE_LABELS = {
  materials: "Materials",
  inventory: "Inventory",
  parts: "Parts (Manufacturing)",
  standard_items: "Standard Items (with BOM)",
  services: "Services",
  installation_systems: "Installation Systems",
  customers: "Customers",
  rental_items: "Rental Items",
  suppliers: "Suppliers",
  rentals: "Rentals",
  service_bookings: "Service Bookings",
  job_cards: "Job Cards",
};

const DATA_TYPE_DESCRIPTIONS = {
  parts: "Import parts for manufacturing. Auto-creates categories and suppliers if they don't exist.",
  standard_items: "Import standard items with Bill of Materials. Each row is one BOM line. Parts will be auto-created if they don't exist. Groups rows by item name/SKU.",
};

const DataManagement = () => {
  const [templates, setTemplates] = useState({});
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [importMode, setImportMode] = useState("add");
  const [selectedFile, setSelectedFile] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetType, setResetType] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [resetStage, setResetStage] = useState(1);
  const [importErrors, setImportErrors] = useState([]);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  
  // Export state
  const [exportType, setExportType] = useState("");
  const [exporting, setExporting] = useState(false);
  
  // Scheduled services XLSX import
  const [scheduledFile, setScheduledFile] = useState(null);
  const [importingScheduled, setImportingScheduled] = useState(false);
  const [scheduledImportResult, setScheduledImportResult] = useState(null);

  const fetchData = async () => {
    try {
      const [templatesRes, statsRes] = await Promise.all([
        axios.get(`${API}/data-management/templates`, { withCredentials: true }),
        axios.get(`${API}/data-management/stats`, { withCredentials: true }),
      ]);
      setTemplates(templatesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load data management info");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDownloadTemplate = async (dataType) => {
    try {
      const response = await axios.get(
        `${API}/data-management/templates/${dataType}`,
        { withCredentials: true }
      );
      
      // Create and download CSV file
      const blob = new Blob([response.data.template], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${dataType}_template.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Downloaded ${dataType} template`);
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleExport = async () => {
    if (!exportType) {
      toast.error("Please select a data type to export");
      return;
    }

    setExporting(true);
    try {
      const response = await axios.get(
        `${API}/data-management/export/${exportType}`,
        { withCredentials: true }
      );
      
      // Create and download CSV file
      const blob = new Blob([response.data.csv_content], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Exported ${response.data.row_count} rows to ${response.data.filename}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadScheduledTemplate = async () => {
    try {
      const response = await axios.get(
        `${API}/service-bookings/import/template`,
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "scheduled_services_template.xlsx";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Downloaded scheduled services template");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleImportScheduledServices = async () => {
    if (!scheduledFile) {
      toast.error("Please select an Excel file");
      return;
    }

    setImportingScheduled(true);
    setScheduledImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", scheduledFile);

      const response = await axios.post(
        `${API}/service-bookings/import`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      setScheduledImportResult(response.data);
      if (response.data.imported_count > 0) {
        toast.success(`Imported ${response.data.imported_count} scheduled services`);
      }
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} rows had errors`);
      }
      setScheduledFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import scheduled services");
    } finally {
      setImportingScheduled(false);
    }
  };

  const handleImport = async () => {
    if (!selectedType || !selectedFile) {
      toast.error("Please select a data type and file");
      return;
    }

    setImporting(true);
    setImportErrors([]);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await axios.post(
        `${API}/data-management/import/${selectedType}?mode=${importMode}`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.success) {
        const { imported_count, error_count, errors } = response.data;
        
        if (imported_count > 0) {
          toast.success(`Successfully imported ${imported_count} ${DATA_TYPE_LABELS[selectedType] || selectedType} records`);
        }
        
        if (error_count > 0 && errors && errors.length > 0) {
          setImportErrors(errors);
          setShowErrorDialog(true);
          toast.warning(`${error_count} row(s) had errors and were skipped`);
        } else if (imported_count === 0 && error_count === 0) {
          toast.info("No records found in the file to import");
        }
        
        fetchData();
        setSelectedFile(null);
        setSelectedType("");
      }
    } catch (error) {
      // Handle different error types
      const errorResponse = error.response?.data;
      
      if (errorResponse?.detail) {
        // Check if it's a header validation error
        if (errorResponse.detail.includes("Invalid headers")) {
          const errorMsg = errorResponse.detail;
          const expectedMatch = errorMsg.match(/Expected: \[(.*?)\]/);
          const gotMatch = errorMsg.match(/Got: \[(.*?)\]/);
          
          const expected = expectedMatch ? expectedMatch[1].replace(/'/g, "") : "";
          const got = gotMatch ? gotMatch[1].replace(/'/g, "") : "";
          
          setImportErrors([
            "CSV header validation failed:",
            `Expected columns: ${expected}`,
            `Found columns: ${got}`,
            "",
            "Please ensure your CSV file has the correct column headers.",
            "Download the template to see the expected format."
          ]);
          setShowErrorDialog(true);
        } else {
          // Generic error
          setImportErrors([errorResponse.detail]);
          setShowErrorDialog(true);
        }
        toast.error("Import failed - see details");
      } else {
        toast.error("Import failed - please check your file format");
      }
    } finally {
      setImporting(false);
    }
  };

  const openResetDialog = (dataType) => {
    setResetType(dataType);
    setResetStage(1);
    setConfirmationCode("");
    setResetDialogOpen(true);
  };

  const handleReset = async () => {
    if (resetStage === 1) {
      setResetStage(2);
      return;
    }

    // Generate expected code
    const today = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const expectedCode = `DELETE-${resetType.toUpperCase()}-${today}`;

    if (confirmationCode !== expectedCode) {
      toast.error("Invalid confirmation code");
      return;
    }

    try {
      await axios.delete(
        `${API}/data-management/reset/${resetType}?confirmation_code=${encodeURIComponent(confirmationCode)}`,
        { withCredentials: true }
      );
      toast.success(`Successfully reset ${resetType} data`);
      setResetDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reset failed");
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="data-management-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Data Management</h1>
        <p className="text-sm text-slate-500 mt-1">
          Import, export, and manage your data
        </p>
      </div>

      {/* Import Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Import Data
          </CardTitle>
          <CardDescription>
            Upload CSV files to import data. Download templates first to ensure correct format.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Data Type</Label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger data-testid="import-type-select">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(DATA_TYPE_LABELS).map((type) => (
                    <SelectItem key={type} value={type}>
                      {DATA_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Import Mode</Label>
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger data-testid="import-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Add to existing data</SelectItem>
                  <SelectItem value="replace">Replace all data</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>CSV File</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                data-testid="import-file-input"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleImport}
              disabled={!selectedType || !selectedFile || importing}
              data-testid="import-btn"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import Data
                </>
              )}
            </Button>
            {selectedType && (
              <Button
                variant="outline"
                onClick={() => handleDownloadTemplate(selectedType)}
                data-testid="download-template-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            )}
          </div>
          
          {/* Type-specific description */}
          {selectedType && DATA_TYPE_DESCRIPTIONS[selectedType] && (
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-sm text-blue-700">
              <strong>Note:</strong> {DATA_TYPE_DESCRIPTIONS[selectedType]}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Export Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export Data
          </CardTitle>
          <CardDescription>
            Export existing data as CSV. The exported file matches the import template format, allowing you to make bulk edits and re-import.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Data Type</Label>
              <Select value={exportType} onValueChange={setExportType}>
                <SelectTrigger data-testid="export-type-select">
                  <SelectValue placeholder="Select type to export..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(DATA_TYPE_LABELS).map((type) => (
                    <SelectItem key={type} value={type}>
                      {DATA_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleExport}
                disabled={!exportType || exporting}
                data-testid="export-btn"
              >
                {exporting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Export to CSV
                  </>
                )}
              </Button>
            </div>
          </div>
          {exportType && (
            <p className="text-sm text-slate-500">
              Export all {DATA_TYPE_LABELS[exportType]} data. The CSV can be edited and re-imported using the "Replace all data" mode.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Scheduled Services Import */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Import Scheduled Services
          </CardTitle>
          <CardDescription>
            Upload an Excel file to schedule services for customers. Customers will be notified via email.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Excel File (.xlsx)</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setScheduledFile(e.target.files?.[0] || null)}
                data-testid="scheduled-import-file"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button
                onClick={handleImportScheduledServices}
                disabled={!scheduledFile || importingScheduled}
                data-testid="scheduled-import-btn"
              >
                {importingScheduled ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Import Services
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={handleDownloadScheduledTemplate}
                data-testid="scheduled-template-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>
          </div>
          
          {scheduledImportResult && (
            <div className="rounded-lg border p-4 space-y-2">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">
                    {scheduledImportResult.imported_count} services imported
                  </span>
                </div>
                {scheduledImportResult.error_count > 0 && (
                  <div className="flex items-center gap-2 text-amber-600">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="text-sm">
                      {scheduledImportResult.error_count} errors
                    </span>
                  </div>
                )}
              </div>
              
              {scheduledImportResult.errors?.length > 0 && (
                <div className="mt-2 text-sm text-red-600 space-y-1">
                  {scheduledImportResult.errors.map((err, i) => (
                    <p key={`sched-err-${i}`}>{err}</p>
                  ))}
                </div>
              )}
              
              {scheduledImportResult.bookings?.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium text-slate-700 mb-1">Imported bookings:</p>
                  <div className="text-sm text-slate-600 space-y-1">
                    {scheduledImportResult.bookings.map((b, i) => (
                      <p key={`import-booking-${b.booking_id || i}`}>
                        {b.customer_name} - {b.service_name} on {b.date} ({b.time})
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Data Overview
          </CardTitle>
          <CardDescription>
            Current record counts and management options
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data Type</TableHead>
                <TableHead className="text-center">Records</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(DATA_TYPE_LABELS).map(([type, label]) => (
                <TableRow key={type} data-testid={`data-row-${type}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4 text-slate-400" />
                      {label}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                      {stats[type] || 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadTemplate(type)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => openResetDialog(type)}
                        disabled={stats[type] === 0}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Reset All Data */}
      <Card className="border-red-200 bg-red-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-800">
            <AlertTriangle className="h-5 w-5" />
            Danger Zone
          </CardTitle>
          <CardDescription className="text-red-600">
            Reset all imported data. This action cannot be undone.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="destructive"
            onClick={() => openResetDialog("all")}
            data-testid="reset-all-btn"
          >
            <RefreshCcw className="h-4 w-4 mr-2" />
            Reset All Data
          </Button>
        </CardContent>
      </Card>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Data Reset
            </DialogTitle>
            <DialogDescription>
              {resetStage === 1 ? (
                <>
                  You are about to delete all <strong>{DATA_TYPE_LABELS[resetType] || resetType}</strong> data.
                  This action cannot be undone.
                </>
              ) : (
                <>
                  To confirm, enter the following code:
                  <code className="block mt-2 p-2 bg-slate-100 rounded text-sm font-mono">
                    DELETE-{resetType.toUpperCase()}-{new Date().toISOString().split("T")[0].replace(/-/g, "")}
                  </code>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {resetStage === 2 && (
            <div className="py-4">
              <Label htmlFor="confirmation">Confirmation Code</Label>
              <Input
                id="confirmation"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                placeholder="Enter code..."
                data-testid="reset-confirmation-input"
              />
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReset} data-testid="confirm-reset-btn">
              {resetStage === 1 ? "Continue" : "Delete Data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Errors Dialog */}
      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Import Errors
            </DialogTitle>
            <DialogDescription>
              The following errors occurred during import. Some rows may have been skipped.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 my-4">
            <div className="space-y-2 bg-slate-50 p-4 rounded-md border">
              {importErrors.map((error, index) => (
                <div 
                  key={`err-row-${index}`} 
                  className={`text-sm ${error === "" ? "h-2" : "text-slate-700"} ${error.startsWith("Row") ? "font-mono text-red-600" : ""}`}
                >
                  {error || " "}
                </div>
              ))}
            </div>
            {importErrors.length >= 10 && (
              <p className="text-xs text-slate-500 mt-2 italic">
                Only showing first 10 errors. There may be more issues in your file.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowErrorDialog(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setShowErrorDialog(false);
              if (selectedType) {
                handleDownloadTemplate(selectedType);
              }
            }}>
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataManagement;
